import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbCalendar, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { UsuarioService } from '@account/usuario.service';
import { EstadoPedido } from '@shared/enums/estadoPedido';
import { Producto } from '@shared/models/abm/Producto';
import { Pedido } from '@shared/models/stock/Pedido';
import { UltimoEstado } from '@shared/models/stock/UltimoEstado';
import { MessageService } from '@shell/message.service';
import { StockService } from '../../../stock.service';
import { BotonGrupo } from '@shared/models/cierreTurno/BotonesGrupo';
import { CierreTurnoService } from '@cierreTurno/cierre-turno.service';
import { CustomValidatorsService } from '@shared/validators/custom-validators.service';
import { isEmptyValue } from '@shared/compat-tools';

@Component({
  selector: 'app-nuevo-pedido-combustible',
  templateUrl: './nuevo-pedido-combustible.component.html',
  styleUrls: ['./nuevo-pedido-combustible.component.scss']
})
export class NuevoPedidoCombustibleComponent implements OnInit {

  @Input() productos: Producto[];

  form: FormGroup;
  guardandoDatos: boolean=false;
  editando: boolean=false;
  modelFecha: NgbDateStruct;
  modelHora: any;
  fecha: Date;
  botonesGrupo: BotonGrupo[];
  seleccionados: number[] = [];
  listaEmails: string[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private calendar: NgbCalendar,
    private usuarioService: UsuarioService,
    private messageService: MessageService,
    private stockService: StockService,
    private cierreTurnoService: CierreTurnoService,
    private fb: FormBuilder) { }

  ngOnInit(): void {
    this.botonesGrupo = this.cierreTurnoService.botonesGrupo;
    this.crearFormulario();
    this.setearFechas();
  }

  getCodigoAeroplanta(){
    return this.usuarioService.getAeroplantaActual().codigoAeroplanta;
  }

  setearFechas() {
    this.modelFecha = this.calendar.getToday();
    this.modelHora = this.calendar.getToday();
    this.fecha = new Date(this.modelFecha.year, this.modelFecha.month-1, this.modelFecha.day);
    const hora = new Date();
    this.modelHora = { hour: hora.getHours(), minute: hora.getMinutes(), second: hora.getSeconds() };
  }

  crearFormulario() {
    this.form = this.fb.group({
      fechaFormControl: [''],
      horaFormControl: [''],
      productoFormControl: ['', Validators.required],      
      cantidadFormControl: [null, [Validators.required, Validators.min(1)]],
      emailFormControl: ['', CustomValidatorsService.emailValidator]
    });
  }

  async guardar() {
    if (!this.form.valid) {
      this.messageService.showErrorMessage('Los datos ingresados no son válidos.');
      return
    }

    if ((this.listaEmails.length === 0) && (this.botonesGrupo.filter(b => b.seleccionado).length === 0)) {
      this.messageService.showErrorMessage('El pedido no tiene destinatarios.');
      return
    }

    try {
      const pedido = new Pedido();
      pedido.fechaHora = this.formatDate(
        this.fechaFormControl.value.year, this.fechaFormControl.value.month,
        this.fechaFormControl.value.day, this.horaFormControl.value.hour, this.horaFormControl.value.minute, 0);

      pedido.producto = this.productoFormControl.value;
      pedido.cantidadPedida = Number(this.cantidadFormControl.value);
      pedido.gruposContacto = this.botonesGrupo
                                .filter(boton => boton.seleccionado)
                                .map(boton => boton.valor);
      pedido.emails = this.listaEmails;
      const estado = new UltimoEstado();
      estado.fechaHora = "2020-01-15T21:00:00.00";
      estado.estado = EstadoPedido.Abierto;
      estado.responsable = this.stockService.obtenerStringResponsable();

      pedido.ultimoEstado = estado;

      this.guardandoDatos = true;
      const r = await this.stockService.postPedidos(this.getCodigoAeroplanta(), pedido);
      if (r) {
        this.messageService.showSuccessMessage(`El pedido fue creado correctamente.`);
        this.activeModal.close(pedido);
        this.guardandoDatos = false;
      }
    } catch (error) {
      this.messageService.showErrorMessage('Hubo un error al crear un pedido.'+'\n'+error.message);
      this.guardandoDatos = false;
    }
  }

  /** Utiles */

  agregarMailDestinatario() {
    if (!this.emailFormControl.valid || isEmptyValue(this.emailFormControl.value)) {
      this.messageService.showErrorMessage('Debe ingresar un email válido.');
      return
    }

    if (this.listaEmails.includes(this.emailFormControl.value)) {
      this.messageService.showErrorMessage('Ya se encuentra el destinatario.');
      return
    }

    this.listaEmails.push(this.emailFormControl.value);
    this.emailFormControl.setValue(null);
  }

  borrarMailDestinatario(email:string) {
    this.listaEmails = this.listaEmails.filter(e => e != email);
  }

  seleccionarGrupo(boton: BotonGrupo) {
    boton.seleccionado = !boton.seleccionado;
    this.seleccionados = this.botonesGrupo
      .filter(b => b.seleccionado)
      .map(b => b.valor);
  }

  formatDate(year, month, day, hour, minute, seconds): string {
    return year + '-' + month.toLocaleString(undefined, { minimumIntegerDigits: 2 }) + '-' + day.toLocaleString(undefined, { minimumIntegerDigits: 2 })
      + 'T' + hour.toLocaleString(undefined, { minimumIntegerDigits: 2 }) + ':' + minute.toLocaleString(undefined, { minimumIntegerDigits: 2 }) + ':' + seconds.toLocaleString(undefined, { minimumIntegerDigits: 2 });
  }

  compareProductos(p1: any, p2: any): boolean {
    return p1 && p2 ? p1.codigoProducto === p2.codigoProducto : p1 === p2;
  }

  get fechaFormControl() {
    return this.form.controls.fechaFormControl;
  }
  
  get horaFormControl() {
    return this.form.controls.horaFormControl;
  }

  get productoFormControl() {
    return this.form.controls.productoFormControl;
  }

  get cantidadFormControl() {
    return this.form.controls.cantidadFormControl;
  }
  
  get emailFormControl() {
    return this.form.controls.emailFormControl;
  }


}
